import React, { useMemo, useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./SalonHome.css";
import { useLocation, useNavigate } from "react-router-dom";
import { sendRequest } from "../ApiRequest";
import { sendCachedRequest } from "../utils/apiCache";
import { BASE_URL } from "../URL/constants";
import Login from "./buttons/Login";
import SignupModal from "./buttons/SignupModal";
import getCurrentUser from "../components/common/GetCurrentUser";
import useHtmlLinkNavigation from "../hooks/useHtmlLinkNavigation";

const SALON_KEYWORDS = ["salon", "saloon", "spa", "wellness", "beauty", "service"];
const SALON_TIME_SLOTS = [
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00"
];

const formatCurrency = (value = 0) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

// Function to convert 24-hour time to 12-hour format with AM/PM
const formatTimeToAMPM = (time24) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

// Function to convert 12-hour time to 24-hour format
const convertTo24Hour = (hour12, minute, period) => {
  let hour24 = parseInt(hour12, 10);
  if (period === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const SalonHome = ({ 
  carouselHtml = [],
  tagline = "",
  companyName = "Looks Salon",
  bodyHtml = [],
  branches: initialBranches = []
}) => {
  const { currentUser } = getCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const bodyContentRef = useHtmlLinkNavigation(bodyHtml);

  const [salonServices, setSalonServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  const [branches, setBranches] = useState(initialBranches);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [activeServiceCategory, setActiveServiceCategory] = useState(null);
  const [servicesByCategory, setServicesByCategory] = useState({});
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingMessage, setBookingMessage] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  
  const todayDate = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [bookingData, setBookingData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    branchId: "",
    gender: "",
    date: todayDate,
    time: "10:00",
    notes: ""
  });

  // Login and Signup Modal states
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const customerData = currentUser?.data?.customer ?? null;

  // Carousel settings
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    fade: true,
  };

  // Add default image fallback
  const defaultImg = "https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80";

  const mapSalonServiceData = useCallback(
    (item) => ({
      id: item.id,
      item_name: item.item_name,
      sales_price: item.sales_price,
      sales_discount: item.sales_discount || 0,
      image: item.item_images?.[0] || defaultImg,
      category: item.category || "Salon Essentials",
      duration: item.service_time_minutes || item.salon_service_duration_minutes || item.estimated_duration || 0,
      description:
        item.short_description ||
        item.description ||
        item.item_subtitle ||
        item.tagline ||
        "",
    }),
    [defaultImg]
  );

  const fetchSalonServices = useCallback(
    async (categoryId = null) => {
      try {
        setServicesLoading(true);
        setServicesError(null);
        
        const response = await sendCachedRequest(
          "GET",
          `${BASE_URL}/v1/public_site/menus/item_lists`,
          null,
          {
            params: {
              item_type: "service",
              per_page: 200,
              ...(categoryId ? { category_id: categoryId } : {})
            },
            cacheKey: categoryId ? `item_lists_services_${categoryId}` : "item_lists_services_all"
          }
        );

        const items = response?.data?.items || [];

        const filtered = items
          .filter((item) => {
            const type = (item?.item_type || "").toLowerCase();
            const category = (item?.category || "").toLowerCase();
            const isQuantityUnlimited = Number(item?.quantity) === -1;
            return (
              isQuantityUnlimited ||
              item?.is_service === true ||
              SALON_KEYWORDS.some((keyword) => type.includes(keyword) || category.includes(keyword))
            );
          })
          .map((item) => mapSalonServiceData(item));

        const dataset =
          filtered.length > 0
            ? filtered
            : items.map((item) => mapSalonServiceData(item));

        if (categoryId) {
          setServicesByCategory((prev) => ({
            ...prev,
            [categoryId]: dataset
          }));
        }

        setSalonServices(dataset);
        
        // Store all services if no category filter
        if (!categoryId) {
          setAllServices(dataset);
        }
      } catch (fetchError) {
        console.error("Failed to load salon services:", fetchError);
        setServicesError("Unable to load salon services right now.");
        setSalonServices([]);
      } finally {
        setServicesLoading(false);
      }
    },
    [mapSalonServiceData]
  );
  
  // Store all services when fetched without category
  const [allServices, setAllServices] = useState([]);

  const fetchServiceCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        {
          params: { item_type: "service" },
          cacheKey: "service_menu_categories"
        }
      );

      const fetchedCategories = Array.isArray(response?.data?.categories)
        ? response.data.categories
        : [];

      // Verify each category has items by fetching items for that category (in parallel)
      const categoryVerificationPromises = fetchedCategories.map(async (category) => {
        const categoryId = String(category?.id);
        if (!categoryId) return null;

        try {
          // Fetch items for this category to verify it has items
          const itemsResponse = await sendCachedRequest(
            "GET",
            `${BASE_URL}/v1/public_site/menus/item_lists`,
            null,
            {
              params: {
                item_type: "service",
                category_id: categoryId,
                per_page: 1 // Only need to check if at least one item exists
              },
              cacheKey: `category_verify_${categoryId}`
            }
          );

          const items = itemsResponse?.data?.items || [];
          
          // Check if category has valid items
          if (items.length > 0) {
            const hasValidItems = items.some((item) => {
              const type = (item?.item_type || "").toLowerCase();
              const itemCategory = (item?.category || "").toLowerCase();
              const isQuantityUnlimited = Number(item?.quantity) === -1;
              return (
                isQuantityUnlimited ||
                item?.is_service === true ||
                SALON_KEYWORDS.some((keyword) => type.includes(keyword) || itemCategory.includes(keyword))
              );
            });

            if (hasValidItems || items.length > 0) {
              return category;
            }
          }
          return null;
        } catch (error) {
          // If error fetching items for category, skip it
          console.warn(`Failed to verify items for category ${categoryId}:`, error);
          return null;
        }
      });

      // Wait for all verifications to complete
      const verifiedCategories = await Promise.all(categoryVerificationPromises);
      const categoriesWithItems = verifiedCategories.filter((cat) => cat !== null);

      setServiceCategories(categoriesWithItems);

      // Default to "All Services" (null)
      setActiveServiceCategory(null);
      fetchSalonServices(null);
    } catch (catError) {
      console.error("Failed to fetch service categories:", catError);
      setServiceCategories([]);
      setCategoriesError("Unable to load service categories right now.");
      setActiveServiceCategory(null);
      fetchSalonServices();
    } finally {
      setCategoriesLoading(false);
    }
  }, [fetchSalonServices]);

  const handleCategoryChange = useCallback(
    (categoryId) => {
      // categoryId can be null for "All Services"
      if (categoryId === activeServiceCategory) return;
      setActiveServiceCategory(categoryId);
      const servicesSection = document.querySelector(".salon-services-section");
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [activeServiceCategory]
  );

  const activeCategoryLabel = useMemo(() => {
    if (!activeServiceCategory) return "";
    const match = serviceCategories.find(
      (category) => String(category.id) === String(activeServiceCategory)
    );
    return match?.name || "";
  }, [activeServiceCategory, serviceCategories]);

  // Branches are now fetched from public_site_info/get_list API in parent component
  // This is kept as a fallback only if branches weren't loaded from public_site_info
  const fetchBranches = useCallback(async () => {
    // Only fetch if we don't have branches yet (from props)
    if (branches.length > 0) {
      return;
    }
    
    try {
      const response = await sendRequest(
        "GET",
        `${BASE_URL}/v1/public_site/branches`
      );
      const branchesData = response.data?.branches || [];
      if (branchesData.length > 0) {
        setBranches(branchesData);
      }
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  }, [branches.length]);

  useEffect(() => {
    fetchServiceCategories();
  }, [fetchServiceCategories]);

  useEffect(() => {
    if (activeServiceCategory === null) {
      // Show all services
      if (allServices.length > 0) {
        setSalonServices(allServices);
      } else {
        fetchSalonServices(null);
      }
      return;
    }
    
    if (activeServiceCategory) {
      if (servicesByCategory[activeServiceCategory]) {
        setSalonServices(servicesByCategory[activeServiceCategory]);
        return;
      }
      fetchSalonServices(activeServiceCategory);
    }
  }, [activeServiceCategory, fetchSalonServices, servicesByCategory, allServices]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    if (branches.length > 0 && !bookingData.branchId) {
      setBookingData((prev) => ({
        ...prev,
        branchId: String(branches[0].id),
        city: branches[0].city || prev.city
      }));
    }
  }, [branches, bookingData.branchId]);

  useEffect(() => {
    // Update branches when initialBranches prop changes
    if (initialBranches.length > 0) {
      setBranches(initialBranches);
    }
  }, [initialBranches]);

  const openBookingForm = useCallback((serviceId = null) => {
    setBookingMessage(null);
    if (serviceId) {
      const normalizedId = Number(serviceId);
      setSelectedServiceIds((prev) => (prev.includes(normalizedId) ? prev : [...prev, normalizedId]));
    } else {
      setSelectedServiceIds([]);
    }
    setBookingFormOpen(true);
  }, []);

  const shouldAutoOpenBooking = location.state?.openBooking;

  useEffect(() => {
    if (shouldAutoOpenBooking) {
      openBookingForm();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [shouldAutoOpenBooking, location.pathname, openBookingForm, navigate]);

  useEffect(() => {
    window.scrollToBooking = (serviceId) => openBookingForm(serviceId);
    
    // Listen for custom event to open booking form from Header
    const handleOpenBookingForm = () => {
      openBookingForm();
    };
    
    window.addEventListener('open-booking-form', handleOpenBookingForm);
    
    return () => {
      delete window.scrollToBooking;
      window.removeEventListener('open-booking-form', handleOpenBookingForm);
    };
  }, [openBookingForm]);

  const closeBookingForm = useCallback(() => {
    setBookingFormOpen(false);
    setBookingMessage(null);
  }, []);

  const handleBookingFieldChange = (event) => {
    const { name, value } = event.target;
    setBookingData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "branchId") {
        const branch = branches.find((branch) => String(branch.id) === value);
        updated.city = branch?.city || prev.city;
      }
      return updated;
    });
  };

  const handlePhoneInput = (event) => {
    const value = event.target.value;
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // If first digit is entered, validate it should be 6, 7, 8, or 9
    if (digitsOnly.length > 0) {
      const firstDigit = digitsOnly[0];
      if (!['6', '7', '8', '9'].includes(firstDigit)) {
        // Don't allow invalid first digit
        return;
      }
    }
    
    // Limit to 10 digits
    const limitedValue = digitsOnly.slice(0, 10);
    
    setBookingData((prev) => ({
      ...prev,
      phone: limitedValue
    }));
  };

  const toggleSalonServiceSelection = (serviceId) => {
    const normalizedId = Number(serviceId);
    setSelectedServiceIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((id) => id !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const handleSalonBookingSubmit = async (event) => {
    event.preventDefault();
    if (!bookingData.name.trim()) {
      setBookingMessage({ type: "error", text: "Please enter your name." });
      return;
    }
    const phone = bookingData.phone.trim();
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setBookingMessage({ type: "error", text: "Please enter a valid 10-digit Indian phone number (starting with 6, 7, 8, or 9)." });
      return;
    }
    if (selectedServiceIds.length === 0) {
      setBookingMessage({ type: "error", text: "Please select at least one service." });
      return;
    }

    setBookingLoading(true);
    setBookingMessage(null);

    const payload = {
      appointment: {
        name: bookingData.name.trim(),
        phone: phone,
        email: bookingData.email.trim(),
        city: bookingData.city,
        branch_id: bookingData.branchId || (branches[0]?.id ? String(branches[0].id) : null),
        gender: bookingData.gender,
        date: bookingData.date,
        time: bookingData.time,
        notes: bookingData.notes,
        service_ids: selectedServiceIds
      }
    };

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/appointments`,
        payload
      );
      const successMessage = response?.data?.message || "Appointment booked successfully.";
      setBookingMessage({ type: "success", text: successMessage });
      
      // Close modal after successful booking (with a small delay to show success message)
      setTimeout(() => {
        closeBookingForm();
        setSelectedServiceIds([]);
        setBookingData((prev) => ({
          ...prev,
          name: "",
          phone: "",
          email: "",
          notes: ""
        }));
      }, 1500);
    } catch (error) {
      const serverErrors = error?.data?.errors;
      const message = Array.isArray(serverErrors)
        ? serverErrors.join(", ")
        : serverErrors || "Failed to book appointment. Please try again.";
      setBookingMessage({ type: "error", text: message });
    } finally {
      setBookingLoading(false);
    }
  };

  // Login Modal handlers
  const closeLoginModal = () => {
    setLoginModalOpen(false);
  };
  
  const handleLoginSuccess = () => {
    closeLoginModal();
    navigate('/customer-info');
  };

  // SignupModal handlers
  const closeSignupModal = () => {
    setSignupModalOpen(false);
    setShowOtpLogin(false);
    setPhoneNumber("");
    setOtp("");
    setFirstName("");
    setLastName("");
    setEmail("");
    // Close Bootstrap modal
    const modal = document.getElementById('signupModal');
    if (modal && typeof window !== 'undefined' && window.bootstrap) {
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
  };

  // Effect to control SignupModal
  useEffect(() => {
    if (signupModalOpen) {
      const modal = document.getElementById('signupModal');
      if (modal && typeof window !== 'undefined' && window.bootstrap) {
        const bootstrapModal = new window.bootstrap.Modal(modal);
        bootstrapModal.show();
        
        // Listen for modal close event
        const handleModalHidden = () => {
          setSignupModalOpen(false);
          setShowOtpLogin(false);
          setPhoneNumber("");
          setOtp("");
          setFirstName("");
          setLastName("");
          setEmail("");
        };
        
        modal.addEventListener('hidden.bs.modal', handleModalHidden);
        
        // Cleanup function
        return () => {
          modal.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
      }
    }
  }, [signupModalOpen]);

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      console.log("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/send_otp`,
        { phone: phoneNumber }
      );

      if (response.data) {
        setShowOtpLogin(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      console.log("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/verify_otp`,
        { phone: phoneNumber, otp: otp }
      );

      if (response.data) {
        // Check if user exists
        if (response.data.user_exists) {
          // User exists, log them in
          localStorage.setItem("userDetails", JSON.stringify(response.data));
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          if (modalElement && window.bootstrap) {
            const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
              modalInstance.hide();
            }
          }
          closeSignupModal();
          
          setTimeout(() => {
            navigate('/customer-info');
          }, 300);
        } else {
          // New user, show signup fields
          setActiveTab("signup");
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      console.log("Please fill in all required fields");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/signup`,
        {
          phone: phoneNumber,
          first_name: firstName,
          last_name: lastName,
          email: email,
          otp: otp
        }
      );

      if (response.data) {
        localStorage.setItem("userDetails", JSON.stringify(response.data));
        
        // Close modal properly
        const modalElement = document.getElementById("signupModal");
        if (modalElement && window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        }
        closeSignupModal();
        
        setTimeout(() => {
          navigate('/customer-info');
        }, 300);
      }
    } catch (error) {
      console.error("Error signing up:", error);
    }
  };

  return (
    <div className="home-page salon-home">
      <span id="booking" style={{ position: "absolute", top: "-100px" }} />
      {carouselHtml.length > 0 && (
        <div className="carousel-wrapper salon-carousel-wrapper">
          <Slider {...settings}>
            {carouselHtml.map((image, index) => (
              <div key={`salon-carousel-${index}`} className="carousel-slide">
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
          </Slider>
        </div>
      )}

      {tagline && (
        <section
          className="salon-hero-content"
          dangerouslySetInnerHTML={{ __html: tagline }}
        />
      )}

      <section className="salon-services-section">
        <div className="container">
          <div className="salon-services-header">
            <h2>Our Salon Services</h2>
            <p>Curated experiences powered by your {companyName} catalog.</p>
          </div>

          {/* Category Filter */}
          {!servicesLoading && !categoriesLoading && serviceCategories.length > 0 && (
            <div className={`salon-categories-filter ${serviceCategories.length + 1 > 5 ? 'salon-categories-slider' : ''}`}>
              <button
                className={`salon-category-btn ${activeServiceCategory === null ? "active" : ""}`}
                onClick={() => handleCategoryChange(null)}
              >
                All Services
              </button>
              {serviceCategories.map((category) => {
                const categoryId = String(category.id);
                const isActive = activeServiceCategory === categoryId;
                return (
                  <button
                    key={categoryId}
                    className={`salon-category-btn ${isActive ? "active" : ""}`}
                    onClick={() => handleCategoryChange(categoryId)}
                  >
                    {category?.name || "Category"}
                    {typeof category?.items_count === "number" && (
                      <span className="salon-category-count">{category.items_count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {categoriesLoading && (
            <div className="salon-category-loading">
              <span className="shimmer" />
              <span className="shimmer" />
              <span className="shimmer" />
            </div>
          )}

          {categoriesError && (
            <div className="salon-error">{categoriesError}</div>
          )}

          {servicesError && (
            <div className="salon-error">
              {servicesError}
            </div>
          )}
          {servicesLoading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Preparing your curated services...</p>
            </div>
          ) : salonServices.length > 0 ? (
            salonServices.length > 4 ? (
              <div className="salon-services-slider-container">
                <Slider
                  dots={true}
                  infinite={false}
                  speed={500}
                  slidesToShow={Math.min(4, salonServices.length)}
                  slidesToScroll={1}
                  arrows={true}
                  swipeToSlide={true}
                  draggable={true}
                  responsive={[
                    {
                      breakpoint: 1200,
                      settings: {
                        slidesToShow: Math.min(3, salonServices.length),
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 992,
                      settings: {
                        slidesToShow: Math.min(3, salonServices.length),
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 768,
                      settings: {
                        slidesToShow: 2,
                        slidesToScroll: 1,
                      },
                    },
                    {
                      breakpoint: 480,
                      settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                      },
                    },
                  ]}
                  className="salon-services-slider"
                >
                  {salonServices.map((service) => {
                    const finalPrice = formatCurrency(service.sales_price - (service.sales_discount || 0));
                    const originalPrice = service.sales_discount ? formatCurrency(service.sales_price) : null;

                    return (
                      <div key={service.id} className="salon-service-slide">
                        <div className="salon-service-card">
                          <div
                            className="salon-service-media"
                            // onClick={() => navigate(`/products/${service.id}`)}
                          >
                            <img src={service.image} alt={service.item_name} />
                          </div>
                          <div className="salon-card-body">
                            <div className="salon-card-meta">
                              <span className="salon-service-category">{service.category}</span>
                              <span className="salon-service-duration">{service.duration} min</span>
                            </div>
                            <h3 className="salon-service-name">{service.item_name}</h3>
                            <p className="salon-service-desc">{service.description}</p>
                            <div className="salon-service-meta">
                              <div className="salon-service-pricing">
                                <span className="salon-price-final">{finalPrice}</span>
                                {originalPrice && (
                                  <span className="salon-price-original">{originalPrice}</span>
                                )}
                              </div>
                            </div>
                            <div className="salon-service-actions">
                              <button
                                className="salon-btn primary"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  openBookingForm(service.id);
                                }}
                              >
                                Book Appointment
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            ) : (
              <div className="salon-services-grid">
                {salonServices.map((service) => {
                const finalPrice = formatCurrency(service.sales_price - (service.sales_discount || 0));
                const originalPrice = service.sales_discount ? formatCurrency(service.sales_price) : null;

                return (
                  <div key={service.id} className="salon-service-card">
                    <div
                      className="salon-service-media"
                      // onClick={() => navigate(`/products/${service.id}`)}
                    >
                      <img src={service.image} alt={service.item_name} />
                      {/* <span className="salon-service-pill">Most Loved</span> */}
                    </div>
                    <div className="salon-card-body">
                      <div className="salon-card-meta">
                        <span className="salon-service-category">{service.category}</span>
                        <span className="salon-service-duration">{service.duration} min</span>
                      </div>
                      <h3 className="salon-service-name">{service.item_name}</h3>
                      <p className="salon-service-desc">{service.description}</p>
                      <div className="salon-service-meta">
                        <div className="salon-service-pricing">
                          <span className="salon-price-final">{finalPrice}</span>
                          {originalPrice && (
                            <span className="salon-price-original">{originalPrice}</span>
                          )}
                        </div>
                        <div className="salon-service-rating">
                          {/* <span className="salon-rating-badge">★ 4.9</span> */}
                          {/* <span className="salon-rating-copy">Guest favourite</span> */}
                        </div>
                      </div>
                      <div className="salon-service-actions">
                        <button
                          className="salon-btn primary"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openBookingForm(service.id);
                          }}
                        >
                          Book Appointment
                        </button>
                        {/* <button
                          className="salon-btn secondary"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleCart(service);
                          }}
                        >
                          Quick Add
                        </button> */}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )
          ) : (
            <div className="no-products">
              <p>
                {activeCategoryLabel
                  ? `No services found in "${activeCategoryLabel}" category.`
                  : "No salon services available right now."}
              </p>
            </div>
          )}
        </div>
      </section>

      {bodyHtml && (
        <section
          className="salon-body-content"
          ref={bodyContentRef}
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      )}

      <Login
        isOpen={loginModalOpen}
        onClose={closeLoginModal}
        onLoginSuccess={handleLoginSuccess}
        customerData={customerData}
      />

      <SignupModal
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        handleSignUp={handleSignUp}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />

      {bookingFormOpen && (
        <div className="salon-booking-modal-overlay" onClick={closeBookingForm}>
          <div className="salon-booking-modal" onClick={(event) => event.stopPropagation()}>
            <div className="salon-booking-header">
              <div>
                {/* <p className="salon-booking-label">Looks Salon Concierge</p> */}
                <h3>Book Your Appointment</h3>
                <p className="salon-booking-subtitle">Our team will confirm your slot shortly.</p>
              </div>
              <button type="button" className="salon-booking-close" onClick={closeBookingForm}>
                ×
              </button>
            </div>

            <form className="salon-booking-form" onSubmit={handleSalonBookingSubmit}>
              <div className="salon-booking-grid">
                <div>
                  <label htmlFor="bookingName">Full Name *</label>
                  <input
                    id="bookingName"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    value={bookingData.name}
                    onChange={handleBookingFieldChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingPhone">Phone Number *</label>
                  <input
                    id="bookingPhone"
                    name="phone"
                    type="tel"
                    placeholder="10-digit mobile (6,7,8,9)"
                    maxLength="10"
                    value={bookingData.phone}
                    onChange={handlePhoneInput}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingEmail">Email</label>
                  <input
                    id="bookingEmail"
                    name="email"
                    type="text"
                    placeholder="you@email.com"
                    value={bookingData.email}
                    onChange={handleBookingFieldChange}
                  />
                </div>
                <div>
                  <label htmlFor="bookingGender">Preferred Menu *</label>
                  <select
                    id="bookingGender"
                    name="gender"
                    value={bookingData.gender}
                    onChange={handleBookingFieldChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Ladies">Ladies</option>
                    <option value="Gents">Gents</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="bookingBranch">Salon Location *</label>
                  <select
                    id="bookingBranch"
                    name="branchId"
                    value={bookingData.branchId}
                    onChange={handleBookingFieldChange}
                    required
                  >
                    <option value="">Select branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.city ? `• ${branch.city}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="bookingCity">City</label>
                  <input
                    id="bookingCity"
                    name="city"
                    type="text"
                    placeholder="Preferred city"
                    value={bookingData.city}
                    onChange={handleBookingFieldChange}
                  />
                </div>
                <div>
                  <label htmlFor="bookingDate">Appointment Date *</label>
                  <input
                    id="bookingDate"
                    name="date"
                    type="date"
                    min={todayDate}
                    value={bookingData.date}
                    onChange={handleBookingFieldChange}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="bookingTime">Preferred Time *</label>
                  <input
                    id="bookingTime"
                    name="time"
                    type="time"
                    value={bookingData.time}
                    onChange={handleBookingFieldChange}
                    onClick={(e) => {
                      try {
                        e.target.showPicker?.();
                      } catch (error) {
                        // Fallback if showPicker is not supported
                      }
                    }}
                    style={{ 
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                    required
                  />
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Click to select hours, minutes & AM/PM
                  </small>
                </div>
              </div>

              <div className="salon-booking-services">
                <label>Select Services *</label>
                {salonServices.length > 0 ? (
                  <div className="salon-booking-service-grid">
                    {salonServices.map((service) => {
                      const isSelected = selectedServiceIds.includes(Number(service.id));
                      return (
                        <button
                          key={service.id}
                          type="button"
                          className={`salon-chip ${isSelected ? "selected" : ""}`}
                          onClick={() => toggleSalonServiceSelection(service.id)}
                        >
                          <span className="salon-chip-name">{service.item_name}</span>
                          <span className="salon-chip-price">{formatCurrency(service.sales_price)}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="salon-booking-empty">Salon services are loading...</p>
                )}
              </div>

              <div>
                <label htmlFor="bookingNotes">Special Requests</label>
                <textarea
                  id="bookingNotes"
                  name="notes"
                  rows="3"
                  placeholder="Share any looks, inspirations, or requirements..."
                  value={bookingData.notes}
                  onChange={handleBookingFieldChange}
                />
              </div>

              {bookingMessage && (
                <div className={`salon-booking-message ${bookingMessage.type}`}>
                  {bookingMessage.text}
                </div>
              )}

              <div className="salon-booking-actions">
                <button type="submit" disabled={bookingLoading}>
                  {bookingLoading ? "Booking..." : "Confirm Appointment"}
                </button>
                <button type="button" className="salon-booking-cancel" onClick={closeBookingForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalonHome;

